type CacheEntry<T> = {
  value?: T;
  expiresAt: number;
  request?: Promise<T>;
};

const entries = new Map<string, CacheEntry<unknown>>();

/**
 * Reuses fresh responses and, importantly, the same in-flight promise. The
 * latter prevents React Strict Mode remounts and sibling pages from issuing
 * duplicate reads while a request is already running.
 */
export function cachedRequest<T>(
  key: string,
  load: () => Promise<T>,
  ttlMs: number,
  force = false,
): Promise<T> {
  const existing = entries.get(key) as CacheEntry<T> | undefined;
  if (existing?.request) return existing.request;
  if (!force && existing?.value !== undefined && existing.expiresAt > Date.now()) {
    return Promise.resolve(existing.value);
  }

  const request = load()
    .then((value) => {
      // An invalidation may have happened while the request was in flight
      // (most importantly during logout). Only the request that still owns
      // this cache entry may publish its response.
      const current = entries.get(key) as CacheEntry<T> | undefined;
      if (current?.request === request) {
        entries.set(key, { value, expiresAt: Date.now() + ttlMs });
      }
      return value;
    })
    .catch((error: unknown) => {
      const current = entries.get(key) as CacheEntry<T> | undefined;
      if (current?.request === request) {
        if (existing?.value !== undefined) entries.set(key, existing);
        else entries.delete(key);
      }
      throw error;
    });

  entries.set(key, {
    value: existing?.value,
    expiresAt: existing?.expiresAt ?? 0,
    request,
  });
  return request;
}

export function getCachedValue<T>(key: string): T | undefined {
  const existing = entries.get(key) as CacheEntry<T> | undefined;
  if (existing?.value !== undefined && existing.expiresAt > Date.now()) {
    return existing.value as T;
  }
  return undefined;
}

export function setCachedValue<T>(key: string, value: T, ttlMs: number): void {
  entries.set(key, { value, expiresAt: Date.now() + ttlMs });
}

export function updateCachedValues<T>(prefix: string, update: (value: T) => T): void {
  for (const [key, entry] of entries) {
    if (!key.startsWith(prefix) || entry.value === undefined) continue;
    const typed = entry as CacheEntry<T>;
    entries.set(key, { ...typed, value: update(typed.value as T) });
  }
}

export function invalidateCache(prefix?: string): void {
  if (!prefix) {
    entries.clear();
    return;
  }
  for (const key of entries.keys()) {
    if (key.startsWith(prefix)) entries.delete(key);
  }
}

